import { useMutation, useQuery } from "@tanstack/react-query";
import { sortBy } from "lodash";
import { useMemo } from "react";
import uuid from "uuid/v4";

import {
  gql,
  GraphQLAPI,
  GraphQLResult,
  queries,
  fragments,
} from "@examspark/api";
import {
  formatClassroomActivityKey,
  getTagFromResource,
  SystemTag,
} from "@examspark/common";
import { defaultClassroomExamConfigData } from "@examspark/exam";
import {
  ExamConfigData,
  IDSK,
  SearchableResourceConnectionNew,
} from "@examspark/types";

import { useAuthorizationContext } from "../../hooks/useAuthorizationContext";
import { useAppContext } from "../../providers/AppContext";

import {
  useMutationCreateClassroomActivity,
  useQueryListClassroomUnitActivities,
} from "./activity";

export const useQuerySearchLowmanExamsForUnit = (unitId: string) => {
  const authorizationContext = useAuthorizationContext();
  const [state] = useAppContext();

  return useQuery({
    queryKey: ["exams", unitId],
    enabled: !!state.user?.id && !!unitId,
    queryFn: async () => {
      const result = (await GraphQLAPI.query({
        query: queries.search.search({
          label: "SearchExams",
          fields: fragments.exam.examConfigFragment,
        }),
        variables: {
          authorizationContext,
          sort: [
            {
              field: "updatedAt",
              direction: "desc",
            },
          ],
          filter: {
            sk: {
              eq: "exam#v0",
            },
            or: [
              {
                referencesIds: {
                  eq: `${unitId}`,
                },
              },
              {
                tags: {
                  eq: `unit-id#${unitId}`,
                },
              },
            ],
            workspaceId: {
              ne: state.workspaceUser?.workspace?.id,
            },
          },
        },
      })) as GraphQLResult<{
        search: SearchableResourceConnectionNew;
      }>;

      return result?.data?.search?.items || [];
    },
  });
};

export const useQuerySearchUserExamsForUnit = (unitId: string) => {
  const authorizationContext = useAuthorizationContext();
  const [state] = useAppContext();

  return useQuery({
    queryKey: ["user-exam-configs", unitId, state.user?.id],
    enabled: !!state.user?.id,
    queryFn: async () => {
      const result = (await GraphQLAPI.query({
        query: queries.search.search({
          label: "SearchUserExams",
          fields: fragments.exam.examConfigFragment,
        }),
        variables: {
          authorizationContext,
          sort: [
            {
              field: "updatedAt",
              direction: "desc",
            },
          ],
          filter: {
            sk: {
              eq: "exam-config",
            },
            creatorId: {
              eq: state.user?.id,
            },
            and: [
              {
                tags: {
                  eq: `unit-id#${unitId}`,
                },
              },
            ],
            workspaceId: {
              eq: state.workspaceUser?.workspace?.id,
            },
          },
        },
      })) as GraphQLResult<{
        search: SearchableResourceConnectionNew;
      }>;

      return result?.data?.search?.items || [];
    },
  });
};

/**
 * Copies an `exam` to a new teacher-owned `exam`, and creates an `exam-config`
 * that may later be assign to students.
 *
 * This method creates:
 *  - `exam`
 *  - `exam-config`
 *  - `classroom-activity`
 *  - `exam-ticket`
 */
export const useMutationCreateClassroomActivityFromExam = () => {
  const authorizationContext = useAuthorizationContext();
  const createClassroomActivityMutation = useMutationCreateClassroomActivity();
  const [state] = useAppContext();

  return useMutation({
    mutationFn: async (input: {
      classroom: IDSK;
      examKey: IDSK;
      unitId: string;
      ltiContextId?: string;
      useOriginalExam?: boolean;
    }) => {
      const { classroom, examKey, unitId } = input;
      const workspaceId = state.workspaceUser?.workspace?.id;
      const creatorId = state.user?.id;
      const appId = state.appLicense?.appId;
      const appKey = state.appLicense?.appKey;
      if (!workspaceId) {
        console.error("No workspaceId found");
        return;
      }
      if (!creatorId) {
        console.error("No creatorId found");
        return;
      }
      if (!appId) {
        console.error("No appId found");
        return;
      }
      if (!appKey) {
        console.error("No appKey found");
        return;
      }

      const examConfigConfig: ExamConfigData = {
        ...defaultClassroomExamConfigData,
        ...(state.app?.config?.examConfigDefaults ?? {}),
        allowRostering: true,
      };

      const examConfigTags = [
        {
          key: "classroom-id",
          value: classroom.id,
          type: "string",
        },
        {
          key: "cohort-classroom",
          value: classroom.id,
          type: "string",
        },
        {
          key: "cohort-school",
          value: "school-xxx-id",
          type: "string",
        },
        {
          key: "unit-id",
          value: input.unitId,
          type: "string",
        },
      ];

      if (input.ltiContextId) {
        examConfigTags.push({
          key: SystemTag.LtiContextId,
          value: input.ltiContextId,
          type: "string",
        });
      }

      // HUGE TODO: Lowman doens't have any user-content banks, so we're totally just
      // lying here
      const bankId = `${workspaceId}/user-content`;

      const examConfig = await gql.operations.exam.createExamFromExamTemplate(
        examKey,
        {
          id: uuid(),
          workspaceId,
          creatorId,
          bankId,
          appId,
          appKey,
          config: JSON.stringify(examConfigConfig),
          tags: examConfigTags,
        },
        {
          createAccessCode: true,
          useOriginalExam: input.useOriginalExam,
          ltiContextId: input.ltiContextId,
        },
      );

      if (!examConfig) {
        console.error("No examConfig found");
        return;
      }

      // create `classroom-activity` record
      const createClassroomActivityResult =
        await createClassroomActivityMutation.mutateAsync({
          ...formatClassroomActivityKey(classroom.id, examConfig.id, unitId),
          schemaVersion: "2020-01-01",
          workspaceId: workspaceId,
          creatorId: authorizationContext.userId,
          resourceId: [examConfig.id, "exam-config"].join("/"),
        });

      if (!createClassroomActivityResult) {
        console.error("No createClassroomActivityResult found");
        return;
      }

      return {
        examConfig: examConfig,
        classroomActivity: createClassroomActivityResult,
      };
    },
  });
};

export const useQueryListClassroomUnitWorksheets = (
  classroomId?: string,
  unitId?: string,
  workspaceId?: string,
) => {
  const {
    data: lowmanExams,
    refetch: refetchExams,
    isFetching: isFetchingExams,
  } = useQuerySearchLowmanExamsForUnit(unitId || "");
  const {
    data: userActivities,
    refetch: refetchActivities,
    isFetching: isFetchingActivities,
  } = useQueryListClassroomUnitActivities(workspaceId, classroomId, unitId);

  const sortedExams: any = useMemo(() => {
    return sortBy(
      [...(lowmanExams ?? []), ...(userActivities ?? [])]?.map(
        (examOrActivity) => {
          const exam =
            examOrActivity.sk === "exam#v0"
              ? examOrActivity
              : examOrActivity.examConfig?.resource;

          const dayTag = getTagFromResource(exam, "day");
          const paddedLeft = (dayTag?.value || "99")?.padStart(2, "0");
          // @ts-expect-error no sequence
          examOrActivity.sequence = paddedLeft || 99;

          if (examOrActivity.sk !== "exam#v0") {
            examOrActivity.tags = examOrActivity.tags || [];
          }
          return examOrActivity;
        },
      ) ?? [],
      "sequence",
    );
  }, [lowmanExams, userActivities]);

  return {
    data: sortedExams,
    refetch: () => {
      refetchExams();
      refetchActivities();
    },
    isFetching: isFetchingExams || isFetchingActivities,
  };
};
